import React, { useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import adminStyleQualityService, {
  StyleQualitySample,
  StyleQualitySampleReactions,
  StyleQualitySamplesPage,
} from '../../services/api/adminStyleQualityService';
import { TaskyAlert, TaskyBadge, TaskyButton, TaskyDialog, TaskySpinner } from '../tasky';
import { clefCheckDescription, clefCheckLabel } from './clefCheckLabels';

const REACTION_LABELS: {
  key: keyof StyleQualitySampleReactions;
  label: string;
  negative?: boolean;
}[] = [
  { key: 'detail_opened', label: '詳細' },
  { key: 'downloaded', label: 'DL' },
  { key: 'favorited', label: 'お気に入り' },
  { key: 'resized', label: 'リサイズ' },
  { key: 'style_good', label: '良い評価' },
  { key: 'edited', label: '編集', negative: true },
  { key: 'regenerated', label: '作り直し', negative: true },
  { key: 'style_bad', label: '悪い評価', negative: true },
];

const SOURCE_LABELS: Record<string, string> = {
  banner_ads: 'バナー広告',
  chat: 'チャット',
  carousel_ads: 'カルーセル',
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()} ${String(date.getHours()).padStart(2, '0')}:${String(
    date.getMinutes(),
  ).padStart(2, '0')}`;
};

const SampleCard: React.FC<{ sample: StyleQualitySample }> = ({ sample }) => {
  const reactions = REACTION_LABELS.filter(({ key }) => sample.reactions[key]);
  return (
    <div
      className="flex flex-col overflow-hidden rounded-tasky-md border border-warm-border bg-white"
      data-testid={`style-quality-sample-${sample.id}`}
    >
      <a href={sample.image_url} target="_blank" rel="noopener noreferrer" className="block bg-gray-50">
        <img
          src={sample.thumbnail_url || sample.image_url}
          alt=""
          className="aspect-square w-full object-contain"
          loading="lazy"
        />
      </a>
      <div className="flex flex-1 flex-col gap-1.5 p-2 text-xs text-gray-600">
        <div className="flex items-center justify-between gap-2 whitespace-nowrap">
          <span>{formatDate(sample.created_at)}</span>
          <span className="text-gray-400">#{sample.id}</span>
        </div>
        {sample.product ? (
          sample.product.url ? (
            <a
              href={sample.product.url}
              target="_blank"
              rel="noopener noreferrer"
              className="line-clamp-2 font-medium text-primary hover:underline"
              title={sample.product.name}
            >
              {sample.product.name}
            </a>
          ) : (
            <span className="line-clamp-2 font-medium text-dark" title={sample.product.name}>
              {sample.product.name}
            </span>
          )
        ) : (
          <span className="text-gray-400">商品なし</span>
        )}
        <p className="flex min-w-0 items-center gap-1">
          <span className="truncate" title={sample.organization_name ?? undefined}>
            {sample.organization_name ?? '組織なし'}
          </span>
          {sample.generation_source && (
            <span className="shrink-0 text-gray-400">
              · {SOURCE_LABELS[sample.generation_source] ?? sample.generation_source}
            </span>
          )}
        </p>
        <div className="flex flex-wrap gap-1">
          {sample.clef_passed === null ? (
            <TaskyBadge variant="secondary">Clef未判定</TaskyBadge>
          ) : sample.clef_passed ? (
            <TaskyBadge variant="success">Clef合格</TaskyBadge>
          ) : (
            <TaskyBadge variant="destructive">Clef不合格</TaskyBadge>
          )}
          {reactions.map(({ key, label, negative }) => (
            <TaskyBadge key={key} variant={negative ? 'warning' : 'info'}>
              {label}
            </TaskyBadge>
          ))}
        </div>
        {sample.clef_failed_checks.length > 0 && (
          <ul className="flex flex-col gap-0.5 text-red-700" aria-label="不合格の理由">
            {sample.clef_failed_checks.map(({ check, score }) => (
              <li key={check} title={clefCheckDescription(check)}>
                {clefCheckLabel(check)}
                {score !== null && <span className="ml-1 tabular-nums text-red-500">{Math.round(score * 100)}%</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

interface Props {
  style: { style_id: number; name: string } | null;
  days: number;
  onClose: () => void;
}

/**
 * スタイルの点数の根拠になったバナー（期間内に作られ、残っているもの）を 20 枚ずつ見る。
 * 1 枚ごとの反応と Clef 合否、商品・組織を出す。スタイルや期間が変わったら呼び出し側で key を変えて作り直す。
 */
const StyleQualitySamplesDialog: React.FC<Props> = ({ style, days, onClose }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState<StyleQualitySamplesPage | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const styleId = style?.style_id ?? null;

  useEffect(() => {
    if (styleId === null) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    adminStyleQualityService
      .samples(styleId, days, page)
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.response?.data?.message || 'サンプルを読み込めませんでした。');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [styleId, days, page]);

  const lastPage = data?.last_page ?? 1;

  return (
    <TaskyDialog
      open={style !== null}
      onClose={onClose}
      size="xl"
      className="max-w-6xl"
      title={
        style ? (
          <span className="flex flex-wrap items-baseline gap-x-2">
            <span>
              #{style.style_id} {style.name}
            </span>
            <span className="text-sm font-normal text-gray-500">
              直近{days}日{data ? `・${data.total}枚` : ''}
              （削除された画像は出ません）
            </span>
          </span>
        ) : null
      }
      footer={
        <div className="flex w-full items-center justify-center gap-3">
          <TaskyButton
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => p - 1)}
            disabled={isLoading || page <= 1}
          >
            前へ
          </TaskyButton>
          <span className="text-sm tabular-nums text-gray-600">
            {page} / {lastPage}
          </span>
          <TaskyButton
            size="sm"
            variant="outline"
            onClick={() => setPage((p) => p + 1)}
            disabled={isLoading || page >= lastPage}
          >
            次へ
          </TaskyButton>
        </div>
      }
    >
      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}
      {isLoading && !data ? (
        <div className="flex items-center justify-center py-10">
          <TaskySpinner size="md" />
        </div>
      ) : data && data.items.length === 0 ? (
        <p className="py-10 text-center text-gray-500">表示できるバナーがありません</p>
      ) : (
        <div className={`grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5 ${isLoading ? 'opacity-60' : ''}`}>
          {data?.items.map((sample) => (
            <SampleCard key={sample.id} sample={sample} />
          ))}
        </div>
      )}
      {data && data.items.length > 0 && (
        <p className="mt-3 flex items-center gap-1 text-xs text-gray-400">
          <ExternalLink className="h-3 w-3" />
          画像を押すと原寸、商品名を押すと商品ページを開きます
        </p>
      )}
    </TaskyDialog>
  );
};

export default StyleQualitySamplesDialog;
